import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal, Pagination, Spinner } from 'react-bootstrap';
import diseaseService from '../../../services/diseaseService';
import Icon from '../../common/Icons';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import DiseaseImportModal from './DiseaseImportModal';

const emptyForm = { code: '', name: '' };
const PAGE_SIZES = [10, 25, 50, 100];

/** Pages affichées autour de la page courante (±2), avec … pour les trous. */
function pageWindow(page, totalPages) {
  const pages = [];
  for (let p = 1; p <= totalPages; p += 1) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= 2) pages.push(p);
  }
  return pages.reduce((acc, p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) acc.push('gap');
    acc.push(p);
    return acc;
  }, []);
}

export default function HeadDoctorDiseasesPage() {
  const { toast } = useDoctorWorkspace();
  const copy = HEAD_DOCTOR_COPY.DISEASES;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [items, setItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const [showImport, setShowImport] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });

  const loadDiseases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await diseaseService.getDiseases({
        page,
        limit: pageSize,
        ...(search ? { search } : {})
      });
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
        if (res.pagination) {
          setPagination(res.pagination);
          // Le serveur ramène à la dernière page si celle demandée n'existe plus (suppression).
          if (res.pagination.page !== page) setPage(res.pagination.page);
        }
      } else {
        setError(res.message || 'Chargement impossible');
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadDiseases();
  }, [loadDiseases]);

  const stats = useMemo(() => {
    const term = search.trim();
    return {
      count: pagination.total,
      isFiltered: Boolean(term),
      filterTerm: term
    };
  }, [pagination.total, search]);

  const pageFrom = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageTo = Math.min(pagination.page * pagination.limit, pagination.total);

  const openActionMenu = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 220,
        menuHeight: estimateActionMenuHeight(2, { withSeparator: true })
      })
    );
    setActionMenuItem(item);
  };

  const closeActionMenu = () => setActionMenuItem(null);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const openCreate = () => {
    closeActionMenu();
    resetForm();
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ code: item.code || '', name: item.name || '' });
    setModalError('');
    setShowModal(true);
    closeActionMenu();
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setModalError('');
    resetForm();
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setModalError('');
    try {
      const payload = { code: form.code.trim().toUpperCase(), name: form.name.trim() };
      if (!payload.code || !payload.name) {
        throw new Error('Code et nom maladie sont obligatoires.');
      }
      if (editingId) {
        await diseaseService.updateDisease(editingId, payload);
        toast('success', 'Maladie mise à jour.');
      } else {
        await diseaseService.createDisease(payload);
        toast('success', 'Maladie ajoutée.');
      }
      closeModal();
      await loadDiseases();
    } catch (e) {
      setModalError(e.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = Boolean(editingId);
  const showPreview = Boolean(form.code.trim() || form.name.trim());

  const handleImported = async (result) => {
    toast(
      result.created > 0 ? 'success' : 'warning',
      `${result.created} maladie(s) ajoutée(s), ${result.rejected_duplicates} doublon(s) rejeté(s).`
    );
    await loadDiseases();
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer la maladie « ${item.code} — ${item.name} » ?`)) return;
    closeActionMenu();
    try {
      await diseaseService.deleteDisease(item.id);
      toast('success', 'Maladie supprimée.');
      if (editingId === item.id) closeModal();
      await loadDiseases();
    } catch (e) {
      setError(e.message || 'Suppression impossible');
    }
  };

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-dis-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={copy.PAGE_TITLE}
        description={copy.PAGE_SUBTITLE}
        actions={
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="hd-dis-import-btn"
              onClick={() => setShowImport(true)}
            >
              <Icon name="fileExcel" size={16} aria-hidden />
              {copy.IMPORT_BUTTON}
            </button>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        }
      />

      <div className="hd-co-shell">
        <section className="hd-co-stats hd-co-stats--2" aria-label="Synthèse du référentiel">
          <article className="hd-co-stat-card">
            <span className="hd-co-stat-card__label">{copy.STAT_SHOWN}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.count}</span>
            <span className="hd-co-stat-card__hint">{copy.RESULTS_LABEL}</span>
          </article>
          <article
            className={`hd-co-stat-card${stats.isFiltered ? ' hd-co-stat-card--queue' : ' hd-co-stat-card--pending'}`}
          >
            <span className="hd-co-stat-card__label">
              {stats.isFiltered ? copy.STAT_SHOWN_HINT : copy.STAT_TOTAL}
            </span>
            <span className="hd-co-stat-card__value hd-dis-stat-mode">
              {loading ? '—' : stats.isFiltered ? `« ${stats.filterTerm} »` : stats.count}
            </span>
            <span className="hd-co-stat-card__hint">
              {stats.isFiltered ? `${stats.count} correspondance(s)` : 'Référentiel complet'}
            </span>
          </article>
        </section>

        <div className="hd-co-toolbar">
          <div className="hd-co-search-wrap">
            <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
            <input
              type="search"
              className="hd-co-search-input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_PLACEHOLDER}
            />
          </div>
          <div className="hd-co-toolbar-end">
            <button
              type="button"
              className="hd-co-refresh-btn"
              onClick={loadDiseases}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={18} aria-hidden />
            </button>
            <div className="hd-co-toolbar-meta">
              <strong>{loading ? '—' : stats.count}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

        {loading ? (
          <div className="hd-co-state" role="status" aria-live="polite">
            <Spinner animation="border" className="hd-co-state__spinner" />
            <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="hd-co-state hd-co-state--empty">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="alertCircle" size={32} />
            </div>
            <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
            <p className="hd-co-state__text">{copy.EMPTY}</p>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        ) : (
          <div className="hd-co-table-wrap table-responsive">
            <table className="table table-hover align-middle hd-dis-table mb-0">
              <thead>
                <tr>
                  <th>{copy.COL_CODE}</th>
                  <th>{copy.COL_NAME}</th>
                  <th className="text-end hd-dis-table__col-actions">{copy.COL_ACTIONS}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={
                      showModal && editingId === item.id ? 'hd-dis-row--editing' : undefined
                    }
                  >
                    <td>
                      <span className="hd-dis-code">{item.code}</span>
                    </td>
                    <td>
                      <span className="hd-dis-name">{item.name}</span>
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="hd-exam-actions-menu__toggle"
                        onClick={(e) => openActionMenu(e, item)}
                        aria-label={`Actions pour ${item.code}`}
                        title="Actions"
                      >
                        <Icon name="ellipsis-vertical" size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.total > 0 ? (
          <nav className="hd-dis-pager" aria-label="Pagination du référentiel">
            <span className="hd-dis-pager__status">
              {copy.PAGE_STATUS(pageFrom, pageTo, pagination.total)}
            </span>

            <Pagination className="mb-0">
              <Pagination.First onClick={() => setPage(1)} disabled={loading || pagination.page <= 1} />
              <Pagination.Prev
                onClick={() => setPage(pagination.page - 1)}
                disabled={loading || pagination.page <= 1}
              />
              {pageWindow(pagination.page, pagination.totalPages).map((p, index) =>
                p === 'gap' ? (
                  <Pagination.Ellipsis key={`gap-${index}`} disabled />
                ) : (
                  <Pagination.Item
                    key={p}
                    active={p === pagination.page}
                    disabled={loading}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next
                onClick={() => setPage(pagination.page + 1)}
                disabled={loading || pagination.page >= pagination.totalPages}
              />
              <Pagination.Last
                onClick={() => setPage(pagination.totalPages)}
                disabled={loading || pagination.page >= pagination.totalPages}
              />
            </Pagination>

            <label className="hd-dis-pager__size">
              {copy.PER_PAGE}
              <Form.Select
                size="sm"
                style={{ width: 'auto' }}
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                disabled={loading}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Form.Select>
            </label>
          </nav>
        ) : null}
      </div>

      {actionMenuItem ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => openEdit(actionMenuItem)}
            >
              <Icon name="edit" size={15} />
              <span>{copy.ACTION_EDIT}</span>
            </button>
            <div className="hd-exam-actions-menu__separator" />
            <button
              type="button"
              className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
              role="menuitem"
              onClick={() => remove(actionMenuItem)}
            >
              <Icon name="trash" size={15} />
              <span>{copy.ACTION_DELETE}</span>
            </button>
          </div>
        </>
      ) : null}

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        className="hd-dis-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog"
        contentClassName="hd-dis-modal__content"
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="alertCircle" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <span
                className={`hd-dis-modal__mode${
                  isEditing ? ' hd-dis-modal__mode--edit' : ' hd-dis-modal__mode--new'
                }`}
              >
                {isEditing ? copy.MODAL_MODE_EDIT : copy.MODAL_MODE_NEW}
              </span>
              <h2 className="hd-dis-modal__title">
                {isEditing ? copy.MODAL_TITLE_EDIT : copy.MODAL_TITLE_NEW}
              </h2>
              <p className="hd-dis-modal__subtitle mb-0">
                {isEditing ? copy.MODAL_SUBTITLE_EDIT : copy.MODAL_SUBTITLE_NEW}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={closeModal}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>

        <Form onSubmit={submit}>
          <div className="hd-dis-modal__body">
            {modalError ? (
              <Alert variant="danger" className="hd-dis-modal__alert mb-0">
                {modalError}
              </Alert>
            ) : null}

            <div className="hd-dis-modal__fields">
              <div className="hd-dis-modal__field">
                <div className="hd-dis-modal__field-head">
                  <label className="hd-dis-modal__label" htmlFor="hd-dis-code">
                    {copy.FIELD_CODE}
                  </label>
                  <span className="hd-dis-modal__counter">{form.code.length}/20</span>
                </div>
                <input
                  id="hd-dis-code"
                  type="text"
                  className="hd-dis-modal__input hd-dis-modal__input--code"
                  value={form.code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                  }
                  placeholder={copy.FIELD_CODE_PLACEHOLDER}
                  maxLength={20}
                  required
                  autoFocus
                  autoComplete="off"
                />
                <p className="hd-dis-modal__hint">{copy.FIELD_CODE_HINT}</p>
              </div>

              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-dis-name">
                  {copy.FIELD_NAME}
                </label>
                <input
                  id="hd-dis-name"
                  type="text"
                  className="hd-dis-modal__input"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={copy.FIELD_NAME_PLACEHOLDER}
                  maxLength={255}
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            {showPreview ? (
              <div className="hd-dis-modal__preview" aria-live="polite">
                <span className="hd-dis-modal__preview-label">{copy.MODAL_PREVIEW_LABEL}</span>
                <div className="hd-dis-modal__preview-row">
                  <span className="hd-dis-code">{form.code.trim() || '—'}</span>
                  <span className="hd-dis-name">{form.name.trim() || '…'}</span>
                </div>
              </div>
            ) : null}
          </div>

          <div className="hd-dis-modal__footer">
            <button
              type="button"
              className="hd-dis-modal__btn-cancel"
              onClick={closeModal}
              disabled={saving}
            >
              {copy.BTN_CANCEL}
            </button>
            <button type="submit" className="hd-co-create-btn" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  {copy.BTN_SAVE}
                </>
              ) : isEditing ? (
                copy.BTN_UPDATE
              ) : (
                <>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.BTN_CREATE}
                </>
              )}
            </button>
          </div>
        </Form>
      </Modal>

      <DiseaseImportModal
        show={showImport}
        onHide={() => setShowImport(false)}
        onImported={handleImported}
      />
    </div>
  );
}
